import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom';

import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { LoggedUserProvider } from '~/contexts/LoggedUserContext';
import { NotificationProvider } from '~/contexts/NotificationContext';
import { UserAuthProvider } from '~/contexts/UserAuthContext';
import { EHttpStatus } from '~/domain/EHttpStatus';
import { ResponseError } from '~/domain/ResponseError';

import { store } from '../../redux';
import { AppRoutes } from './AppRoutes';
import { theme } from './theme';

const onQueryError = async (error: ResponseError) => {
  if ([EHttpStatus.Unauthorized, EHttpStatus.Forbidden].includes(error.status)) {
    //this is needed in case users revoke access to application in jira
    queryClient.refetchQueries({ queryKey: ['getAccessToken'], exact: true });
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // caches it for 5 seconds only.
      // in case the ui accidentally makes the same request multiple times
      cacheTime: 5000,
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
      onError: onQueryError,
      retry: (failureCount, error) => {
        return failureCount === 0 && [EHttpStatus.Unauthorized, EHttpStatus.Forbidden].includes((error as ResponseError)?.status);
      },
      retryDelay: 2000,
    },
    mutations: {
      useErrorBoundary: false,
      onError: onQueryError,
    },
  },
});

export const App: React.FC = () => {
  return (
    <>
      <BrowserRouter>
        <ReduxProvider store={store}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
              <HelmetProvider>
                <UserAuthProvider>
                  <LoggedUserProvider>
                    <NotificationProvider>
                      <CssBaseline />
                      <AppRoutes />
                    </NotificationProvider>
                  </LoggedUserProvider>
                </UserAuthProvider>
              </HelmetProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </ReduxProvider>
      </BrowserRouter>
    </>
  );
}
